/* What each player actually held, taken from the last recorded tick rather than the wreckage.

   GameOverModal counted cells out of the live metadata, and a finished game has already released
   the cells of everyone eliminated — so the losing side was reported as holding zero cities, which
   is true of the moment and useless as a result. Their last tick knows better.

   `GET /game/{id}/steps/` returns every player's summary once the game is FINISHED (08/D3a), which
   is the case this reads. A live game returns only your own team, so rows for the others simply do
   not appear — no worse than what it replaces.

   The three scores (S25, 2026-09-05) are the server's own: `units_score` values every unit held on
   the cross-team scale the ranking already uses, `buildings_score` every building level, and
   `kills_score` the value of the enemy units this player felled — counted as they fell, so a
   player eliminated on round 20 keeps the kills they made. Each is `null` when the summary does
   not carry it (a game recorded before the field existed), by the same rule as the rest of this
   module: no record is not the same claim as zero. */

const scoreOrNull = (entry, key) => (entry && entry[key] != null ? Number(entry[key]) : null)

export const finalStandingFrom = ({ steps, players }) => {
  const lastWithScores = [...(steps || [])]
    .reverse()
    .find((step) => Object.keys(step?.summary?.scores || {}).length > 0)

  const scores = lastWithScores?.summary?.scores || {}

  return (players || []).map((player) => {
    const entry = scores[String(player.user.id)]
    const army = Object.values(entry?.units || {}).reduce((total, n) => total + n, 0)
    return {
      userId: player.user.id,
      username: player.user.username,
      team: player.team,
      // `null` rather than 0: "we have no record" and "held nothing" are different claims, and
      // showing 0 for the first is the bug this module exists to fix.
      cities: entry ? entry.cells : null,
      army: entry ? army : null,
      food: entry ? entry.food : null,
      armyScore: scoreOrNull(entry, "units_score"),
      buildingsScore: scoreOrNull(entry, "buildings_score"),
      killsScore: scoreOrNull(entry, "kills_score"),
      kills: entry?.kills || null,
      points: player.user.rankingPoints ?? 0,
    }
  })
}

/* Survival's standings are an order, not a table (SV16).

   A classic game's rows are read side by side — two teams, and the winning one is marked. In
   survival every person is on the same side and the only question the screen answers is *who
   lasted longest*, so the order is the result: whoever is still standing first, then the fallen
   from the last round backwards. `eliminatedAtTick` is null while a seat still holds a crown,
   which is why a null sorts ahead of every number rather than behind it as a 0 would.

   A pure function over rows that carry `eliminatedAtTick`, so the modal can order whatever it has
   already assembled, and a stable sort so two people who fell on the same round keep the order the
   server listed them in — a draw has no finer verdict than "the same round", and inventing one by
   id would read as a ranking the game never made. */
export const survivalOrder = (rows) =>
  [...(rows || [])]
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const left = a.row.eliminatedAtTick
      const right = b.row.eliminatedAtTick
      if (left == null && right == null) return a.index - b.index
      if (left == null) return -1
      if (right == null) return 1
      return right - left || a.index - b.index
    })
    .map(({ row }) => row)

/* How many rounds a seat was in the game for. A survivor's answer is the game's own length — they
   were there for all of it — and everybody else's is the round their last crown fell, which is
   what `eliminated_at_tick` records. Null when nothing was recorded (a classic game, a bot, a
   game still running), and the column prints a dash rather than a 0: "we were not told" is not
   the same claim as "fell on round zero". */
export const roundsSurvived = (row, rounds) => {
  if (!row) return null
  if (row.eliminatedAtTick != null) return row.eliminatedAtTick
  return row.eliminated ? null : (rounds ?? null)
}

/* The waves, as one line. A survival game seats a bot every ten rounds, so by round sixty there
   are more enemy rows than the standings were ever built to draw, and none of them is somebody a
   player knows by name. What is worth reading is how many came and how many are down — counted
   off the same `eliminated` flag the roster's strip reads, so the two screens agree. */
export const enemyTally = (players) => {
  const enemies = (players || []).filter((player) => player.user?.isBot)
  return { total: enemies.length, fallen: enemies.filter((player) => player.eliminated).length }
}

export default finalStandingFrom
